import React from "react";
import { PrimaryButton, SecondaryButton, ButtonSizeOptions } from "@loanbossinc/component-library";
import styled from "styled-components";
import moment from "moment";
import { Tooltip } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import DatePicker from "material-ui-pickers/DatePicker";
import { LENDER_REPORTING_DASHBOARD } from "constants/routes";

import Timer from "../../svgs/Timer.svg";

import { CalendarButton } from "./calendarButton";
import { getLinkText } from "./lenderReportingDashboard.helper";
import { BackButtonText } from "./constants";

const LenderDataWrapper = styled.div`
  font-family: Work Sans;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background: inherited;
  padding-top: 48px;
`;

const LenderDataContainerLeft = styled.div`
  display: flex;
  order: 1;
  flex-direction: column;
  gap: 1em;
`;

const LenderDataContainerRight = styled.div`
  display: flex;
  order: 2;
  flex-direction: column;
  justify-content:space-around;
  width: 278px;
  gap: 1em;
  margin-top: -60px;
`;

const ButtonContainer = styled.div`
  width: 100%;
`;

const CompanyName = styled.div`
  font-family: Work Sans;
  font-weight: 500;
  font-size: 36px;
  style: normal;
  color: #000000;
  order: 1;
  padding-bottom: 20px;
`;

const SeeBorrowingEntities = styled.div`
  font-family: Work Sans;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 23px;
  order: 2;
  font-weight: 500;
  color: #005AA9;
  width:195px;
`;

const ApprovalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  order: 3;
`;

const ApprovedBy = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #000000;
`;

const ApprovedByCompany = styled.div`
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  opacity: 50%;
`;

const ApprovedByEmail = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #005AA9;
`;

const ExpireContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5em;
  font-family: Work Sans;
  font-size: 18px;
  font-weight: 500;
  line-height: 23px;
`;

const ExpireContainerLabel = styled.div`
  font-family: Work Sans;
  font-size: 14px;
  margin-bottom: 5px;
`;

const ExpireMessageWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5em;
`;

const ExpireMessage = styled.div`
  font: Work Sans;
  font-weight: 500;
  font-size: 20px;
  font-style: normal;
  color: #000000;
`;

const StyledFilledButton = styled(PrimaryButton)`&& {
  width: 100%;
  height: 48px;
  font-weight: 500;
  font-family: Work Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
}`;
const StyledOutlinedButton = styled(SecondaryButton)`&& {
  width: 100%;
  height: 48px;
  font-weight: 500;
  font-family: Work Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
}`;

const SentOn = styled.div`
  color: #5F6368;
  font-family: Work Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
`;

const ButtonWrapper = styled.div`&& {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 19px;
  font-family: Work Sans;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: #005AA9;
  text-decoration: none;
  cursor: pointer;
}`;

const ButtonText = styled.div`
  font-family: Work Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
`;

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    arrow: true,
    backgroundColor: "#043968",
    color: "white",
    maxWidth: 434,
    fontSize: theme.typography.pxToRem(14)
  }
}))(Tooltip);

const daysBetween = expirationDate => {
  const expiration = moment(expirationDate).format("LL");
  const today = moment().utc().format("LL");
  const diff = moment(expiration).diff(today, "days");

  return Math.trunc(diff);
};

const getExpiryText = (reportUnshared, expirationDate) => {
  if (reportUnshared) {
    return "Access Removed";
  }
  const daysUntilExpiration = daysBetween(expirationDate);
  if (daysUntilExpiration < 0) {
    return "Access Expired";
  }
  if (daysUntilExpiration === 0) {
    return "Expires today";
  }

  return daysUntilExpiration === 1 ? `Expires in ${daysUntilExpiration} day` : `Expires in ${daysUntilExpiration} days`;
};

const goBack = (history, dealId) => {
  switch (history.location.state?.from) {
    case BackButtonText.SUBMITTED.FROM:
      return history.goBack();
    case BackButtonText.LENDER_REPORTS.FROM:
      return history.push(dealId ? `/deal/${dealId}/lender-reports` : "/DealsDashboard");
    default:
      return history.push(LENDER_REPORTING_DASHBOARD);
  }
};

const LenderDataHeader = ({ lenderData, editExpiration, setExpirationDate, expDate, authenticated, downloadReport, history, isLenderView, rgiId, reportUnshared }) => {
  const { deal, approver, expirationDate } = lenderData || {};
  const { name: dealName, borrowingEntities } = deal || {};
  const { name: approverName, email, approverCompanyName } = approver || {};

  return (
    <LenderDataWrapper>
      <LenderDataContainerLeft flex-direction="row">
        {
          authenticated && !isLenderView &&
            <ButtonWrapper onClick={() => goBack(history, deal.id)}>
              <div className="material-icons" style={{ width: "24px" }}>arrow_backwards</div>
              <ButtonText>{getLinkText(history.location.state?.from)}</ButtonText>
            </ButtonWrapper>
        }
        <div>
          <CompanyName>{dealName}</CompanyName>
          {
            authenticated &&
              <HtmlTooltip
                placement="right-start"
                title={<>{borrowingEntities?.map((item, idx) => <div key={idx}>{item}</div>)}</>}
              >
                <SeeBorrowingEntities>See borrowing entities</SeeBorrowingEntities>
              </HtmlTooltip>
          }
        </div>
        {
          approverName &&
            <ApprovalContainer>
              <ApprovedBy>
                {`Approved by ${approverName}`}
              </ApprovedBy>
              <ApprovedByCompany>{approverCompanyName}</ApprovedByCompany>
              <ApprovedByEmail>{email}</ApprovedByEmail>
            </ApprovalContainer>
        }
      </LenderDataContainerLeft>
      <LenderDataContainerRight>
        <div>
          {
            !editExpiration && expirationDate &&
              <ExpireContainer>
                <ExpireMessageWrapper>
                  <img style={{ opacity: "50%" }} src={Timer} />
                  <ExpireMessage>
                    {getExpiryText(reportUnshared, expirationDate)}
                  </ExpireMessage>
                </ExpireMessageWrapper>
              </ExpireContainer>
          }
          {
            editExpiration &&
            (
              <>
                <ExpireContainerLabel>
                  Shared access expires:
                </ExpireContainerLabel>
                <ExpireContainer>
                  <DatePicker
                    TextFieldComponent={props => <CalendarButton {...props} />}
                    style={{ width: "278px", height: "48px" }}
                    format="MMMM DD, YYYY"
                    value={moment(expDate)}
                    onChange={value => {
                      setExpirationDate(moment(value).toDate());
                    }}
                  />
                </ExpireContainer>
              </>
            )
          }
        </div>
        {
          authenticated &&
            <ButtonContainer>
              {isLenderView && <StyledFilledButton
                label={"Download Report"}
                size={ButtonSizeOptions.lgFluid}
                onClick={() => {
                  downloadReport();
                }}
              />}
              {!isLenderView && <StyledOutlinedButton
                label={"Preview Report"}
                size={ButtonSizeOptions.lgFluid}
                onClick={() => history.push(`/lenderReportingDashboard/lender/${rgiId}`)}
              />}
            </ButtonContainer>
        }
      </LenderDataContainerRight>
    </LenderDataWrapper>
  );
};

export default LenderDataHeader;
